import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { PAGE_SIZE } from '@constants';
import type { TourAlbumItem } from '@types';
import TextLink from '@components/TextLink';
import PathArrow from '@components/PathArrow';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import {
  fetchTourAlbums,
  fetchTourItems,
  searchTourAlbums,
  searchTourItems,
} from '@apis/tourApis';
import ItemList from '@components/ItemList';
import { useScenesCopyContext } from './scenesCopyContext';
import ItemRow from './ItemRow';

const sortItems: DropdownItem[] = [
  { id: 0, name: '最新修改' },
  { id: 1, name: '最新创建' },
  { id: 2, name: '名称正序' },
  { id: 3, name: '名称倒序' },
];

export function List() {
  const { albumId, sort, query, setScenesCopyState } = useScenesCopyContext();

  const { data: albumData = {} } = useQuery<{
    albums?: TourAlbumItem[];
    parentId?: number;
    albumName?: string;
  }>(
    ['sc-albums', albumId, sort, query],
    async () => {
      if (query) {
        const resp = await searchTourAlbums({ query });
        return { albums: resp };
      }
      const resp = await fetchTourAlbums({ albumId, sort });
      return {
        albums: resp.children,
        parentId: resp.parentId,
        albumName: resp.name,
      };
    },
    {
      refetchOnWindowFocus: true,
    }
  );

  const {
    data: itemData,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    ['sc-items', albumId, sort, query],
    ({ pageParam = 1 }) => {
      if (query) {
        return searchTourItems({ query, page: pageParam });
      }
      return fetchTourItems({ albumId, page: pageParam, sort });
    },
    {
      refetchOnWindowFocus: true,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < PAGE_SIZE) return undefined;
        return allPages.length + 1;
      },
    }
  );

  const { albumName = '', albums = [], parentId = 0 } = albumData;

  const items = useMemo(
    () => (itemData?.pages ?? []).reduce((acc, cur) => acc.concat(cur), []),
    [itemData?.pages]
  );

  const renderAlbumPath = () => {
    if (query) {
      return <span>所有文件</span>;
    }
    if (albumId === 0) {
      return <span>根目录</span>;
    }
    return (
      <div className="flex items-center">
        <TextLink
          scheme="light"
          className="mr-10"
          onClick={() => setScenesCopyState({ albumId: parentId })}
        >
          返回上一级
        </TextLink>
        <PathArrow />
        <span className="ml-10 max-w-2xl truncate">{albumName}</span>
      </div>
    );
  };
  return (
    <div className="grid grid-rows-[50px_1fr] pr-10">
      <div className="flex h-50 items-center bg-white-light px-20">
        {renderAlbumPath()}
        {!query && (
          <div className="ml-auto">
            <Dropdown
              items={sortItems}
              selected={sort}
              onChange={(val) => setScenesCopyState({ sort: val })}
              getTitleName={(name) => `排序：${name}`}
              scheme="light"
            />
          </div>
        )}
      </div>
      <div className="relative">
        <ItemList
          itemCount={items.length + albums.length}
          itemData={{ items, albums }}
          emptyString="暂无搜索结果"
          hasNextPage={hasNextPage}
          loading={isLoading}
          loadMoreItems={async () => {
            fetchNextPage();
          }}
          row={ItemRow}
        />
      </div>
    </div>
  );
}

export default List;
